import { useState, useRef, useEffect } from 'react';

import CustomStyleSheet from '@prahatech/util-react-web/stylesheet/CustomStyleSheet';
import Displayable from '@prahatech/util-react-web/utils/Displayable';

const genName = () => Date.now() + '-+-' + Math.random() * 1000000;
const stringifyDate = (year, month, day) => `${year}-${month}-${day}T00:00:00.000`;

export function DateInput(props) {
  const { onFieldChange, onFieldFocus, onFieldBlur, key, value, required, error, showErrors, label } = props;

  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [focusInput, setFocusInput] = useState(undefined);

  const [year, setYear] = useState(undefined);
  const [month, setMonth] = useState(undefined);
  const [day, setDay] = useState(undefined);

  const [uniqueFieldName, setUniqueFieldName] = useState(genName());

  useEffect(() => {
    setUniqueFieldName(genName());
  }, [key]);

  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);

  const _clearInput = e => {
    setYear(undefined);
    setMonth(undefined);
    setDay(undefined);

    _changeValue(e, { year: undefined, month: undefined, day: undefined });

    dayRef.current.focus();
  };

  const _changeYear = e => {
    setFocusInput(e.target);
    setCursorPosition(e.target.selectionStart);
    const year = e.target.value;

    if (!/^\d{0,4}$/.test(year)) return;

    setYear(year);
    _changeValue(e, { year, month, day });
  };

  const _changeMonth = e => {
    setFocusInput(e.target);
    setCursorPosition(e.target.selectionStart);
    const month = e.target.value;

    if (!/^\d{0,2}$/.test(month)) return;

    setMonth(month);
    _changeValue(e, { year, month, day });

    if (month.length >= 2) {
      yearRef.current.focus();
    }
  };

  const _changeDay = e => {
    setFocusInput(e.target);
    setCursorPosition(e.target.selectionStart);
    const day = e.target.value;

    if (!/^\d{0,2}$/.test(day)) return;

    setDay(day);
    _changeValue(e, { year, month, day });

    if (day.length >= 2) {
      monthRef.current.focus();
    }
  };

  const _changeValue = (e, { year, month, day }) => {
    if (!year || year.length !== 4 || !month || !day) {
      onFieldChange(undefined);
      typeof props.onChange === 'function' && props.onChange(e);
      return;
    }

    year = String(year).padStart(4, 0);
    month = String(month).padStart(2, 0);
    day = String(day).padStart(2, 0);

    let dateStr = stringifyDate(year, month, day);

    let date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) date = undefined;

    onFieldChange(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toISOString());
    typeof props.onChange === 'function' && props.onChange(e);
  };

  const _focus = e => {
    onFieldFocus();

    typeof props.onFocus === 'function' && props.onFocus(e);
  };

  const _blur = e => {
    onFieldBlur();

    typeof props.onBlur === 'function' && props.onBlur(e);

    setHasBeenTouched(true);
  };

  useEffect(() => {
    if (value === undefined || value === null) return;

    const date = Displayable.date(value);

    if (Number.isNaN(date.getTime())) return;

    const yyyy = date.utc.year.full;
    const mm = date.utc.month.full;
    const dd = date.utc.day.full;

    const currentDateString = stringifyDate(year, month, day);
    const newDateString = stringifyDate(yyyy, mm, dd);

    if (newDateString === currentDateString) return;

    if (Number(yyyy) !== Number(year)) setYear(yyyy);
    if (Number(mm) !== Number(month)) setMonth(mm);
    if (Number(dd) !== Number(day)) setDay(dd);
  }, [value]);

  useEffect(() => {
    if (!focusInput) return;

    focusInput.selectionStart = cursorPosition;
    focusInput.selectionEnd = cursorPosition;
  }, [cursorPosition]);

  const shouldShowErrors = showErrors && hasBeenTouched && (value || required) && error;

  return (
    <div className='core-date-input'>
      <div className='core-date-input__label'>
        {label}
        {required ? <span className='core-date-input__label__required'>*</span> : ''}
      </div>
      <div className='date-inputs'>
        <input
          type='text'
          name={uniqueFieldName + '-dd'}
          autoComplete='new-password'
          className='input_day'
          placeholder={'dd'}
          value={day || ''}
          ref={dayRef}
          onChange={_changeDay}
          onFocus={_focus}
          onBlur={_blur}
        />
        <span className='dot'>•</span>
        <input
          type='text'
          name={uniqueFieldName + '-mm'}
          autoComplete='new-password'
          className='input_month'
          placeholder='mm'
          value={month || ''}
          ref={monthRef}
          onChange={_changeMonth}
          onFocus={_focus}
          onBlur={_blur}
        />
        <span className='dot'>•</span>
        <input
          type='text'
          name={uniqueFieldName + '-yyyy'}
          autoComplete='new-password'
          className='input_year'
          placeholder='yyyy'
          value={year || ''}
          ref={yearRef}
          onChange={_changeYear}
          onFocus={_focus}
          onBlur={_blur}
        />
        {year || month || day ? (
          <div className='core-date-input__clear-btn' onClick={_clearInput}>
            <svg className='core-date-input__clear-icon' viewBox='0 0 102 102' xmlns='http://www.w3.org/2000/svg'>
              <path d='M51 102C79.1665 102 102 79.1665 102 51C102 22.8335 79.1665 0 51 0C22.8335 0 0 22.8335 0 51C0 79.1665 22.8335 102 51 102ZM57.364 51L72.182 65.818C73.9393 67.5754 73.9393 70.4246 72.182 72.182C70.4246 73.9393 67.5754 73.9393 65.818 72.182L51 57.364L36.182 72.182C34.4246 73.9393 31.5754 73.9393 29.818 72.182C28.0607 70.4246 28.0607 67.5754 29.818 65.818L44.636 51L29.818 36.182C28.0607 34.4246 28.0607 31.5754 29.818 29.818C31.5754 28.0607 34.4246 28.0607 36.182 29.818L51 44.636L65.818 29.818C67.5754 28.0607 70.4246 28.0607 72.182 29.818C73.9393 31.5754 73.9393 34.4246 72.182 36.182L57.364 51Z' />
            </svg>
          </div>
        ) : null}
      </div>
      <div className='core-date-input__error'>{shouldShowErrors && <span>{error}</span>}</div>
    </div>
  );
}

const scope = '.core-date-input';
const styles = `
  {
    width: 100%;
    margin: 0 0 conf(spacing, small) 0;
    display: flex;
    flex-direction: column;
  }

  .core-date-input__label {
    font-preset: 1em light;
    color: conf(color, primary);
  }

  .core-date-input__label__required {
    color: conf(color, warn);
    padding: 0 2px;
  }

  .core-date-input__error {
    padding-top: 4px;
    font-preset: .8em light;
    color: conf(color, warn);
  }

  .date-inputs {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  }

  input {
    width: 7ch;
    text-align: center;
    padding: conf(spacing, smaller) 0 conf(spacing, smaller) 0;
    color: conf(color, darkerGrey);
    outline: none; 
    border: none;
    border-bottom: 2px solid conf(color, lightGrey);
  }

  input:focus {
    border-bottom: 2px solid conf(color, primary);
  }

  input::placeholder {
    font-size: 1em;
    text-align: center;
    color: conf(color, grey);
  }

  .dot {
    color: conf(color, lightGrey);
    padding-top: 7px;
    display: flex;
    align-items: center;
  }

  .input_year {
    margin-right: 10px;
  }

  .core-date-input__clear-btn {
    align-self: center;
    justify-self: center;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  .core-date-input__clear-icon {
    fill: conf(color, lightGrey);
    height: 80%;
    width: 80%;
  }

  .core-date-input__clear-btn:hover .core-date-input__clear-icon {
    fill: conf(color, warn);
  }
`;

CustomStyleSheet.create(styles, scope);
